/**
 * PeopleSearch — universal person lookup across every collection.
 * Use when "I don't see them in Pending / Roster / Dropped" — this answers
 * "does the record exist at all, and if so where is it?".
 */
import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
    ArrowLeft,
    Search,
    Loader2,
    User,
    Mail,
    Phone,
    AlertTriangle,
    CalendarCheck,
    ClipboardList,
    UserCheck,
    Activity,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AthleteNameLink from "@/components/coach/AthleteNameLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PeopleSearch() {
    const { user } = useAuth();
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);
    const [results, setResults] = useState(null);

    const runSearch = async (e) => {
        e?.preventDefault?.();
        if (!q || q.length < 2) {
            toast.error("Type at least 2 characters");
            return;
        }
        setBusy(true);
        try {
            const r = await axios.get(`${API}/coach/people/search`, {
                params: { q }, withCredentials: true,
            });
            setResults(r.data);
        } catch (err) {
            toast.error("Search failed", { description: err?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    if (user?.role !== "owner" && user?.role !== "coach") {
        return <div className="p-10 text-center text-zinc-400">Coach access only.</div>;
    }

    const t = results?.totals;
    const totalHits = t ? (t.users + t.pending + t.confirmed + t.dismissed + t.dropped + t.athletes) : 0;

    return (
        <div className="min-h-screen bg-brand-obsidian text-white">
            <div className="max-w-5xl mx-auto px-6 py-8">
                <Link to="/coach" className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 hover:text-white inline-flex items-center gap-1.5">
                    <ArrowLeft className="w-3 h-3" /> Coach console
                </Link>

                <h1 className="font-display text-4xl tracking-wider text-white mt-3 mb-1 flex items-center gap-3">
                    <Search className="w-7 h-7 text-brand-red" /> Find anyone
                </h1>
                <p className="text-sm text-zinc-300 mb-6">
                    Searches every collection — accounts, pending leads, confirmed bookings, dismissed records, honeypot drops, and individual athletes. Use this when someone says they signed up but you can't find them.
                </p>

                <form onSubmit={runSearch} className="flex gap-2 mb-6">
                    <input
                        type="text"
                        autoFocus
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Name, email, or phone…"
                        data-testid="people-search-input"
                        className="flex-1 min-w-0 bg-black border border-zinc-800 px-4 py-3 text-base text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-red"
                    />
                    <button
                        type="submit"
                        disabled={busy || q.length < 2}
                        data-testid="people-search-submit"
                        className="btn-tactical inline-flex flex-shrink-0 disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        <span className="hidden sm:inline">Search</span>
                    </button>
                </form>

                {results && (
                    <div data-testid="people-search-results">
                        <div className="mb-5 text-sm text-zinc-300">
                            <strong className="text-white">{totalHits}</strong> record{totalHits === 1 ? "" : "s"} found for "<span className="text-brand-red">{results.q}</span>"
                            {totalHits === 0 && (
                                <p className="text-amber-300 mt-2">
                                    Nothing in the database matches that term. Either they never submitted, the submission failed before reaching the DB, or the spelling differs. Try last name only, just the email, or just the phone number.
                                </p>
                            )}
                        </div>

                        {results.pending_requests.length > 0 && (
                            <Section icon={ClipboardList} title="In Pending (needs scheduling)" color="text-brand-red" count={results.pending_requests.length}>
                                {results.pending_requests.map((r) => (
                                    <RequestCard key={r.request_id} r={r} status="pending" />
                                ))}
                            </Section>
                        )}

                        {results.confirmed_requests.length > 0 && (
                            <Section icon={CalendarCheck} title="In Confirmed bookings" color="text-emerald-400" count={results.confirmed_requests.length}>
                                {results.confirmed_requests.map((r) => (
                                    <RequestCard key={r.request_id} r={r} status="confirmed" />
                                ))}
                            </Section>
                        )}

                        {results.dropped_requests.length > 0 && (
                            <Section icon={AlertTriangle} title="In Dropped (honeypot quarantine)" color="text-amber-400" count={results.dropped_requests.length}>
                                {results.dropped_requests.map((d) => (
                                    <DroppedRow key={d.dropped_id} d={d} />
                                ))}
                            </Section>
                        )}

                        {(results.raw_submissions || []).length > 0 && (
                            <Section icon={AlertTriangle} title="Failed-validation raw attempts (rescue net)" color="text-red-400" count={results.raw_submissions.length}>
                                {results.raw_submissions.map((r) => (
                                    <RawSubmissionRow key={r.raw_id} r={r} />
                                ))}
                            </Section>
                        )}

                        {results.users.length > 0 && (
                            <Section icon={UserCheck} title="In Roster / Accounts" color="text-brand-navy-200" count={results.users.length}>
                                {results.users.map((u) => (
                                    <UserRow key={u.user_id} u={u} />
                                ))}
                            </Section>
                        )}

                        {results.athletes.length > 0 && (
                            <Section icon={Activity} title="As individual athletes" color="text-purple-300" count={results.athletes.length}>
                                {results.athletes.map((a) => (
                                    <AthleteRow key={a.athlete_id} a={a} />
                                ))}
                            </Section>
                        )}

                        {results.dismissed_requests.length > 0 && (
                            <Section icon={ClipboardList} title="Dismissed assessment requests" color="text-zinc-500" count={results.dismissed_requests.length}>
                                {results.dismissed_requests.map((r) => (
                                    <RequestCard key={r.request_id} r={r} status="dismissed" />
                                ))}
                            </Section>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function Section({ icon: Icon, title, color, count, children }) {
    return (
        <div className="mb-7">
            <div className={`flex items-center gap-2 mb-3 text-[11px] tracking-[0.25em] uppercase font-bold ${color}`}>
                <Icon className="w-4 h-4" /> {title}
                <span className="ml-1 px-2 py-0.5 bg-zinc-900 border border-zinc-800 rounded-full text-[10px] text-zinc-300">{count}</span>
            </div>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

function RequestCard({ r, status }) {
    const borderColor = status === "confirmed" ? "border-emerald-700" : status === "dismissed" ? "border-zinc-800" : "border-brand-red/60";
    return (
        <div className={`border ${borderColor} bg-zinc-950 rounded-lg p-4`}>
            <div className="flex items-start gap-3 mb-2">
                <div className="flex-1 min-w-0">
                    <div className="text-base font-bold text-white truncate">{r.parent_name || "(no name)"}</div>
                    <div className="text-[11px] text-zinc-400 mt-0.5">
                        Submitted {new Date(r.created_at).toLocaleString()} · status: <span className="text-white">{r.status}</span>
                        {r.suspicious_signals?.length > 0 && (
                            <span className="ml-2 text-amber-300">⚠ Flagged: {r.suspicious_signals.join(", ")}</span>
                        )}
                    </div>
                </div>
                <Link to="/coach/assessment-requests" className="text-[10px] tracking-[0.2em] uppercase text-brand-red hover:text-white">Open page →</Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-3 gap-y-1 text-xs text-zinc-200">
                <div className="inline-flex items-center gap-1.5 truncate"><Mail className="w-3 h-3 text-zinc-500" /> {r.email || "—"}</div>
                <div className="inline-flex items-center gap-1.5 truncate"><Phone className="w-3 h-3 text-zinc-500" /> {r.phone || "—"}</div>
                {(r.athletes || []).length > 0 && (
                    <div className="inline-flex items-center gap-1.5 truncate sm:col-span-2">
                        <User className="w-3 h-3 text-zinc-500" /> Athletes: {(r.athletes || []).map((a) => a.name).join(", ")}
                    </div>
                )}
                {(r.notes || "").trim() && (
                    <div className="sm:col-span-2 text-zinc-400">Notes: {r.notes}</div>
                )}
                <div className="sm:col-span-2 text-[10px] text-zinc-500">request_id: {r.request_id} · user_id: {r.user_id}</div>
            </div>
        </div>
    );
}

function DroppedRow({ d }) {
    const name = `${d.parent_first_name || ""} ${d.parent_last_name || ""}`.trim() || "(no name)";
    return (
        <div className="border border-amber-700/50 bg-zinc-950 rounded-lg p-4">
            <div className="text-[10px] tracking-[0.2em] uppercase text-amber-300 mb-1">{d.reason} · {new Date(d.created_at).toLocaleString()}</div>
            <div className="text-base font-bold text-white">{name}</div>
            <div className="grid sm:grid-cols-2 gap-x-3 gap-y-1 text-xs text-zinc-200 mt-2">
                <div className="inline-flex items-center gap-1.5 truncate"><Mail className="w-3 h-3 text-zinc-500" /> {d.email || "—"}</div>
                <div className="inline-flex items-center gap-1.5 truncate"><Phone className="w-3 h-3 text-zinc-500" /> {d.phone || "—"}</div>
                <div className="sm:col-span-2 text-[10px] text-zinc-500">dropped_id: {d.dropped_id} · status: {d.status}</div>
            </div>
            <Link to="/coach/assessment-requests" className="text-[10px] tracking-[0.2em] uppercase text-amber-300 hover:text-white mt-2 inline-block">Open Dropped tab →</Link>
        </div>
    );
}

function UserRow({ u }) {
    const isClient = !["coach", "owner"].includes(u.role);
    const profileUrl = isClient ? `/coach/leads/${u.user_id}` : "/coach/coaches";
    return (
        <div className="border border-zinc-800 bg-zinc-950 rounded-lg p-4">
            <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                    <Link
                        to={profileUrl}
                        data-testid={`people-search-user-${u.user_id}`}
                        className="text-base font-bold text-white truncate block hover:text-brand-red underline-offset-2 hover:underline transition-colors"
                    >
                        {u.name || "(no name)"}
                    </Link>
                    <div className="text-[11px] text-zinc-400 mt-0.5">
                        Role: <span className="text-white">{u.role}</span> · status: <span className="text-white">{u.status || "—"}</span> · created: {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </div>
                </div>
                <Link to={profileUrl} className="text-[10px] tracking-[0.2em] uppercase text-brand-red hover:text-white">{isClient ? "Open profile →" : "Open coaches →"}</Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-3 gap-y-1 text-xs text-zinc-200 mt-2">
                <div className="inline-flex items-center gap-1.5 truncate"><Mail className="w-3 h-3 text-zinc-500" /> {u.email || "—"}</div>
                <div className="inline-flex items-center gap-1.5 truncate"><Phone className="w-3 h-3 text-zinc-500" /> {u.phone || "—"}</div>
                <div className="sm:col-span-2 text-[10px] text-zinc-500">user_id: {u.user_id}</div>
            </div>
        </div>
    );
}

function AthleteRow({ a }) {
    return (
        <div className="border border-zinc-800 bg-zinc-950 rounded-lg p-4">
            <div className="text-base font-bold text-white">
                <AthleteNameLink
                    athleteId={a.athlete_id}
                    name={`${a.first_name || ""} ${a.last_name || ""}`.trim() || "(no name)"}
                    testid={`people-search-athlete-${a.athlete_id}`}
                    className="text-base font-bold text-white hover:text-brand-red underline-offset-2 hover:underline transition-colors"
                />
            </div>
            <div className="text-xs text-zinc-300 mt-1">
                DOB: {a.dob || "—"} · Track: {a.track || "—"} · Sports: {(a.sports || []).join(", ") || "—"}
            </div>
            {a.parent && (
                <div className="text-xs text-zinc-400 mt-1">
                    Parent account: <span className="text-white">{a.parent.name || a.parent.email}</span> ({a.parent.email}) · role: {a.parent.role}
                </div>
            )}
            <div className="text-[10px] text-zinc-500 mt-1">athlete_id: {a.athlete_id}</div>
        </div>
    );
}

function RawSubmissionRow({ r }) {
    // The raw_body is the literal JSON the parent sent. We try to parse it for
    // a cleaner display; if it's malformed, fall back to truncated raw text.
    let parsed = null;
    try { parsed = JSON.parse(r.raw_body || ""); } catch { /* ignore */ }
    const name = parsed
        ? `${(parsed.parent_first_name || "").trim()} ${(parsed.parent_last_name || "").trim()}`.trim()
        : "";
    return (
        <div className="border border-red-700/50 bg-red-950/20 rounded-lg p-4">
            <div className="text-[10px] tracking-[0.2em] uppercase text-red-300 mb-1">
                Failed validation · {new Date(r.received_at).toLocaleString()}
            </div>
            <div className="text-base font-bold text-white">{name || parsed?.email || "(no name)"}</div>
            <div className="text-xs text-zinc-200 mt-1">
                Error: <span className="text-red-300">{r.validation_error || "(see raw payload)"}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-3 gap-y-1 text-xs text-zinc-200 mt-2">
                <div>Email: <span className="text-white">{parsed?.email || "—"}</span></div>
                <div>Phone: <span className="text-white">{parsed?.phone || "—"}</span></div>
                {(parsed?.athletes || []).length > 0 && (
                    <div className="sm:col-span-2">Athletes: {(parsed?.athletes || []).map((a) => `${a.first_name || ""} ${a.last_name || ""}`.trim()).filter(Boolean).join(", ") || "—"}</div>
                )}
                {(parsed?.notes || "").trim() && (
                    <div className="sm:col-span-2 text-zinc-400">Notes: {parsed.notes}</div>
                )}
            </div>
            <details className="mt-2">
                <summary className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 cursor-pointer hover:text-white">Raw payload</summary>
                <pre className="text-[10px] text-zinc-400 bg-black/40 p-2 mt-1 rounded overflow-auto max-h-40 whitespace-pre-wrap break-all">{r.raw_body || ""}</pre>
            </details>
            <div className="text-[10px] text-zinc-500 mt-1">raw_id: {r.raw_id} · IP: {r.submitter_ip}</div>
        </div>
    );
}
